import React, { useMemo, useState } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography, Checkbox, Tooltip } from '@mui/material';
import { motion } from 'framer-motion';
import axios from 'axios';
import axiosInstance from '../AxiosInstance';
import { getNowInPst, getSubmissionStatus } from '../calendar/submissionUtils';
import { getPlanMonthForManagerView, getAllowedMonthStart, getAllowedMonthEnd } from '../calendar/planWeekUtils';

// Calendar availability URL base (test: http://localhost:3000/calendar/availability)
const CALENDAR_AVAILABILITY_BASE = 'https://allcprmanage.com/calendar/availability';

const InstructorTable = ({ currentItems, handleClickOpen, userRole, icpisName, submissionFilter = 'ALL', managerId }) => {
  const isManagerRole = userRole === 'ROLE_ICPIE' || userRole === 'ROLE_ICPIS';
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifyUrl, setNotifyUrl] = useState('');
  const [notifySlackId, setNotifySlackId] = useState('');
  const [notifyInstructorId, setNotifyInstructorId] = useState(null);
  const [notifySlackSent, setNotifySlackSent] = useState(false);
  const [notifySlackSending, setNotifySlackSending] = useState(false);
  const [notifyAllOpen, setNotifyAllOpen] = useState(false);
  const [notifyAllLoading, setNotifyAllLoading] = useState(false);
  const [withSlack, setWithSlack] = useState([]);
  const [withoutSlack, setWithoutSlack] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const buildToken = (instructorId, dateYmd) => {
    const raw = `${instructorId}|${dateYmd}`;
    try {
      return window.btoa(unescape(encodeURIComponent(raw)));
    } catch (e) {
      return window.btoa(raw);
    }
  };

  const todayYmd = () => {
    const d = new Date();
    return d.toISOString().slice(0, 10);
  };

  const openNotifyForInstructor = (instructor) => {
    if (!instructor?.id) return;
    const token = buildToken(instructor.id, todayYmd());
    const url = `${CALENDAR_AVAILABILITY_BASE}?token=${token}`;
    setNotifyUrl(url);
    setNotifySlackId(instructor.slackId || instructor.slack_id || '');
    setNotifyInstructorId(instructor.id);
    setNotifySlackSent(false);
    setNotifySlackSending(false);
    setNotifyOpen(true);
  };

  // Fetch the count of manager target slots for an instructor in the current plan month.
  const fetchTargetSlotCount = async (instructorId) => {
    if (!instructorId || !managerId) return 0;
    try {
      const { year, month } = getPlanMonthForManagerView();
      const monthStart = getAllowedMonthStart(year, month);
      const monthEnd = getAllowedMonthEnd(year, month);
      const res = await axiosInstance.get('/proposal/calendar', {
        params: { instructorId, managerId, monthStart, monthEnd },
      });
      const data = Array.isArray(res.data?.data) ? res.data.data : [];
      return data.filter((ev) => {
        const isRejected = ev.isRejected === true || ev.rejected === true;
        const isCancelled = ev.isCancelled === true || ev.cancelled === true;
        return (ev.sourceType || 'INSTRUCTOR') === 'MANAGER' && !isRejected && !isCancelled;
      }).length;
    } catch (e) {
      console.error('Failed to fetch target slot count', e);
      return 0;
    }
  };

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(notifyUrl);
      alert('URL copied to clipboard');
    } catch (e) {
      console.error(e);
      alert('Failed to copy URL');
    }
  };

  const handleSendToSlack = async () => {
    if (!notifyUrl || !notifySlackId) {
      alert('Missing Slack ID or URL');
      return;
    }
    try {
      setNotifySlackSending(true);
      const urlObj = new URL(notifyUrl);
      const token = urlObj.searchParams.get('token');
      const target_slot_count = await fetchTargetSlotCount(notifyInstructorId);
      await axios.get('https://alhe.app.n8n.cloud/webhook/309cc362-0286-4481-a940-275f0d0166e3', {
        params: { token, sid: notifySlackId, target_slot_count },
      });
      setNotifySlackSent(true);
    } catch (e) {
      console.error(e);
      alert('Failed to send to Slack');
    }
    setNotifySlackSending(false);
  };

  const openNotifyAll = async () => {
    if (!isManagerRole) return;
    setNotifyAllLoading(true);
    try {
      const resp = await axiosInstance.get('/instructor', {
        params: {
          pageNum: 1,
          pageSize: 1000,
          role: 'ROLE_ICPIS',
          icpisname: icpisName,
        },
      });
      if (resp.data?.message !== 'success') {
        alert('Failed to load instructors');
        setNotifyAllLoading(false);
        return;
      }
      const nowPst = getNowInPst();
      const items = (resp.data.data?.items || []).map((ins) => {
        const slackId = ins.slackId ?? ins.slack_id ?? '';
        const status = getSubmissionStatus(ins.submissionTime || ins.submission_time, nowPst);
        return {
          ...ins,
          slackId,
          submissionStatus: status,
        };
      });
      const withS = items.filter((i) => i.slackId);
      const withoutS = items.filter((i) => !i.slackId);
      setWithSlack(withS);
      setWithoutSlack(withoutS);
      setSelectedIds(new Set(withS.map((i) => i.id)));
      setNotifyAllOpen(true);
    } catch (e) {
      console.error(e);
      alert('Failed to load instructors');
    } finally {
      setNotifyAllLoading(false);
    }
  };

  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedCount = withSlack.filter((i) => selectedIds.has(i.id)).length;

  const displayItems = useMemo(() => {
    const nowPst = getNowInPst();
    return Array.isArray(currentItems)
      ? currentItems.map((ins) => {
          const status = getSubmissionStatus(ins.submissionTime || ins.submission_time, nowPst);
          return { ...ins, submissionStatus: status };
        })
      : [];
  }, [currentItems]);

  const handleSendAllToSlack = async () => {
    const selected = withSlack.filter((i) => selectedIds.has(i.id));
    const total = selected.length;
    if (total === 0) {
      alert('No instructors selected');
      return;
    }
    if (!window.confirm(`Send ${total} notifications?`)) return;
    let success = 0;
    let failed = 0;
    const dateYmd = todayYmd();
    for (const ins of selected) {
      try {
        const token = buildToken(ins.id, dateYmd);
        const target_slot_count = await fetchTargetSlotCount(ins.id);
        await axios.get('https://alhe.app.n8n.cloud/webhook/309cc362-0286-4481-a940-275f0d0166e3', {
          params: { token, sid: ins.slackId, target_slot_count },
        });
        success++;
      } catch (e) {
        console.error(e);
        failed++;
      }
    }
    alert(`Notification send: ${success}, failed: ${failed}`);
  };

  return (
    <TableContainer>
      <Table sx={{ minWidth: 650 }}>
        <TableHead>
          <TableRow>
          <TableCell sx={{ fontSize: '0.75rem', padding: '4px', whiteSpace: 'nowrap' }}>Action</TableCell>
            <TableCell sx={{ fontSize: '0.75rem', padding: '4px', whiteSpace: 'nowrap' }}>Id</TableCell>
            <TableCell sx={{ fontSize: '0.75rem', padding: '4px', whiteSpace: 'nowrap' }}>Venue ID</TableCell>
            <TableCell sx={{ fontSize: '0.75rem', padding: '4px', whiteSpace: 'nowrap' }}>FirstName</TableCell>
            <TableCell sx={{ fontSize: '0.75rem', padding: '4px', whiteSpace: 'nowrap' }}>LastName</TableCell>
            <TableCell sx={{ fontSize: '0.75rem', padding: '4px', whiteSpace: 'nowrap' }}>State</TableCell>
            <TableCell sx={{ fontSize: '0.75rem', padding: '20px', whiteSpace: 'nowrap' }}>City</TableCell>
            <TableCell sx={{ fontSize: '0.75rem', padding: '4px', whiteSpace: 'nowrap' }}>PhoneNumber</TableCell>
            <TableCell sx={{ fontSize: '0.75rem', padding: '4px', whiteSpace: 'nowrap' }}>Email</TableCell>
            <TableCell sx={{ fontSize: '0.75rem', padding: '4px', whiteSpace: 'nowrap' }}>Slack ID</TableCell>
            {isManagerRole && (
              <TableCell sx={{ fontSize: '0.75rem', padding: '4px', whiteSpace: 'nowrap' }}>Submitted</TableCell>
            )}
            {isManagerRole && (
              <TableCell sx={{ fontSize: '0.75rem', padding: '4px', whiteSpace: 'nowrap' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={{ fontSize: '0.75rem' }}>Notify</span>
                  <Tooltip title="Notify all instructors managed by you." enterDelay={2000}>
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={openNotifyAll}
                        disabled={notifyAllLoading}
                      >
                        Notify All
                      </Button>
                    </span>
                  </Tooltip>
                </div>
              </TableCell>
            )}
            {isManagerRole  && (
  <>
    {/* <TableCell sx={{ fontSize: '0.75rem', padding: '4px', whiteSpace: 'nowrap' }}>Wage Per Hour</TableCell>
    <TableCell sx={{ fontSize: '0.75rem', padding: '4px', whiteSpace: 'nowrap' }}>Salary Info</TableCell>
    <TableCell sx={{ fontSize: '0.75rem', padding: '4px', whiteSpace: 'nowrap' }}>Total Classes Times</TableCell>
    <TableCell sx={{ fontSize: '0.75rem', padding: '4px', whiteSpace: 'nowrap' }}>Deposit</TableCell>
    <TableCell sx={{ fontSize: '0.75rem', padding: '4px', whiteSpace: 'nowrap' }}>Rent Manikin Numbers</TableCell>
    <TableCell sx={{ fontSize: '0.75rem', padding: '4px', whiteSpace: 'nowrap' }}>Finance</TableCell>
    <TableCell sx={{ fontSize: '0.75rem', padding: '4px', whiteSpace: 'nowrap' }}>Rent Status</TableCell> */}
  </>
)}
            {/* <TableCell sx={{ fontSize: '0.75rem', padding: '4px', whiteSpace: 'nowrap' }}>Fob Key</TableCell> */}
            
          </TableRow>
        </TableHead>
        <TableBody>
        {Array.isArray(displayItems) && displayItems.length > 0 ? (
            displayItems.map((instructor) => (
              <TableRow key={instructor.id}>
                 <TableCell sx={{ fontSize: '0.75rem', padding: '4px', whiteSpace: 'nowrap' }}>
                  <motion.div
                   whileHover={{scale:1.05}}
                   whileTap={{scale:0.8}}
                  
                  >
                  <Button variant="outlined" onClick={() => handleClickOpen(instructor)}>Edit</Button>

                  </motion.div>
                </TableCell>
                <TableCell sx={{ fontSize: '0.75rem', padding: '4px', whiteSpace: 'nowrap' }}>{instructor.id}</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', padding: '4px', whiteSpace: 'nowrap' }}>
                  {Array.isArray(instructor.venues) && instructor.venues.length > 0
                    ? instructor.venues.map((v) => v.id).join(', ')
                    : Array.isArray(instructor.venueIds) && instructor.venueIds.length > 0
                    ? instructor.venueIds.join(', ')
                    : '-'}
                </TableCell>
                <TableCell sx={{ fontSize: '0.75rem', padding: '4px', whiteSpace: 'nowrap' }}>{instructor.firstname}</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', padding: '4px', whiteSpace: 'nowrap' }}>{instructor.lastname}</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', padding: '4px', whiteSpace: 'nowrap' }}>{instructor.state}</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', padding: '4px', whiteSpace: 'nowrap' }}>{instructor.city}</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', padding: '4px', whiteSpace: 'nowrap' }}>{instructor.phoneNumber}</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', padding: '4px', whiteSpace: 'nowrap' }}>{instructor.email}</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', padding: '4px', whiteSpace: 'nowrap' }}>{instructor.slackId || instructor.slack_id || ''}</TableCell>
                {isManagerRole && (
                  <TableCell sx={{ fontSize: '0.75rem', padding: '4px', whiteSpace: 'nowrap' }}>
                    {instructor.submissionStatus === 'YES' && (
                      <span style={{ color: '#2e7d32', fontWeight: 600 }}>YES</span>
                    )}
                    {instructor.submissionStatus === 'NO' && (
                      <span style={{ color: '#d32f2f', fontWeight: 600 }}>NO</span>
                    )}
                    {instructor.submissionStatus === 'EXPIRED' && (
                      <span style={{ color: '#ed6c02', fontWeight: 600 }}>EXPIRED</span>
                    )}
                  </TableCell>
                )}
                {isManagerRole && (
      <>
                <TableCell sx={{ fontSize: '0.75rem', padding: '4px', whiteSpace: 'nowrap' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => openNotifyForInstructor(instructor)}
                  >
                    Notify
                  </Button>
                </TableCell>
                  {/* <TableCell sx={{ fontSize: '0.75rem', padding: '4px', whiteSpace: 'nowrap' }}>{instructor.wageHour}</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', padding: '4px', whiteSpace: 'nowrap' }}>{instructor.salaryInfo}</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', padding: '4px', whiteSpace: 'nowrap' }}>{instructor.totalClassTimes}</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', padding: '4px', whiteSpace: 'nowrap' }}>{instructor.deposit}</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', padding: '4px', whiteSpace: 'nowrap' }}>{instructor.rentManikinNumbers}</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', padding: '4px', whiteSpace: 'nowrap' }}>{instructor.finance}</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', padding: '4px', whiteSpace: 'nowrap' }}>{instructor.rentStatus}</TableCell> */}
  </>
        )}

                {/* <TableCell sx={{ fontSize: '0.75rem', padding: '4px', whiteSpace: 'nowrap' }}>{instructor.fobKey}</TableCell> */}
               
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={16} align="center">No data available</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <Dialog open={notifyAllOpen} onClose={() => setNotifyAllOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Notify All Instructors</DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Instructors with slack (Total count: {selectedCount})
          </Typography>
          {withSlack.length === 0 ? (
            <Typography variant="body2" sx={{ mb: 2 }}>
              No instructors with Slack.
            </Typography>
          ) : (
            <Table size="small" sx={{ mb: 2 }}>
              <TableHead>
                <TableRow>
                  <TableCell>First Name</TableCell>
                  <TableCell>Last Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Slack ID</TableCell>
                  <TableCell>Submitted</TableCell>
                  <TableCell align="center">Select</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {withSlack.map((ins) => (
                  <TableRow key={ins.id}>
                    <TableCell>{ins.firstname}</TableCell>
                    <TableCell>{ins.lastname}</TableCell>
                    <TableCell>{ins.email}</TableCell>
                    <TableCell>{ins.slackId}</TableCell>
                    <TableCell>
                      {ins.submissionStatus === 'YES' && (
                        <span style={{ color: '#2e7d32', fontWeight: 600 }}>YES</span>
                      )}
                      {ins.submissionStatus === 'NO' && (
                        <span style={{ color: '#d32f2f', fontWeight: 600 }}>NO</span>
                      )}
                      {ins.submissionStatus === 'EXPIRED' && (
                        <span style={{ color: '#ed6c02', fontWeight: 600 }}>EXPIRED</span>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Checkbox
                        checked={selectedIds.has(ins.id)}
                        onChange={() => toggleSelected(ins.id)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Instructors without slack
          </Typography>
          {withoutSlack.length === 0 ? (
            <Typography variant="body2">
              No instructors without Slack.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>First Name</TableCell>
                  <TableCell>Last Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Slack ID</TableCell>
                  <TableCell align="center">Select</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {withoutSlack.map((ins) => (
                  <TableRow key={ins.id}>
                    <TableCell>{ins.firstname}</TableCell>
                    <TableCell>{ins.lastname}</TableCell>
                    <TableCell>{ins.email}</TableCell>
                    <TableCell>{ins.slackId}</TableCell>
                    <TableCell align="center">
                      <Checkbox checked={false} disabled />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSendAllToSlack} disabled={withSlack.length === 0}>
            Send
          </Button>
          <Button onClick={() => setNotifyAllOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={notifyOpen} onClose={() => setNotifyOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Notify Instructor</DialogTitle>
        <DialogContent>
          {notifySlackSent ? (
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
              Successful! Sent to slack.
            </Typography>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Share this URL with the instructor:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  p: 1,
                  borderRadius: 1,
                  bgcolor: '#f5f5f5',
                  wordBreak: 'break-all',
                  fontFamily: 'monospace',
                  fontSize: 12,
                }}
              >
                {notifyUrl || 'N/A'}
              </Typography>
              {notifySlackId && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Slack ID: {notifySlackId}
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {notifySlackSent ? (
            <Button
              onClick={() => {
                setNotifySlackSent(false);
                setNotifyOpen(false);
              }}
              autoFocus
            >
              Confirm
            </Button>
          ) : (
            <>
              <Button onClick={handleCopyUrl} disabled={!notifyUrl}>
                Copy
              </Button>
              <Button
                onClick={handleSendToSlack}
                disabled={!notifyUrl || !notifySlackId || notifySlackSending}
              >
                {notifySlackSending ? 'Sending...' : 'Send to Slack'}
              </Button>
              <Button
                onClick={() => {
                  setNotifySlackSent(false);
                  setNotifyOpen(false);
                }}
              >
                Close
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </TableContainer>
  );
};
export default InstructorTable;